import { buildCopyContextMenuItems, gridHeaderIconsSx, useClearCellRangeOnOutsideClick } from '@/components/ReportDashboard/Shared/GridActionsMenu'
import { COMPANY_COLORS, getColorWithOpacity } from '@/constants/colors.constant'
import { GfrItem } from '@/constants/ReportDashboard/gfr-items'
import { useDarkModeCheck, useTheme } from '@/contexts/RADashboardThemeContext/hooks'
import { FactorLensFocus } from '@/interfaces/factorLens.interface'
import {
  cardPrefsContextKey,
  FactorContrib,
  FactorLensPrefs,
  RADashboardCardPrefs,
} from '@/interfaces/raDashboard.interface'
import { putUserPreference } from '@/services/userPreference.service'
import { getHeader } from '@/utils/factorAnalytics.util'
import { getGridThemeClass, sideBarDefaultConfig } from '@/utils/grid-utils/ag-grid-config'
import { validateInput } from '@/utils/UserInputValidation'
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model'
import {
  ColDef,
  ColumnMovedEvent,
  ColumnState,
  GridReadyEvent,
  ICellRendererParams,
  ModuleRegistry,
  ToolPanelVisibleChangedEvent,
} from '@ag-grid-community/core'
import { CsvExportModule } from '@ag-grid-community/csv-export'
import { AgGridReact } from '@ag-grid-community/react'
import { ClipboardModule } from '@ag-grid-enterprise/clipboard'
import { ColumnsToolPanelModule } from '@ag-grid-enterprise/column-tool-panel'
import { ExcelExportModule } from '@ag-grid-enterprise/excel-export'
import { FiltersToolPanelModule } from '@ag-grid-enterprise/filter-tool-panel'
import { MenuModule } from '@ag-grid-enterprise/menu'
import { RangeSelectionModule } from '@ag-grid-enterprise/range-selection'
import { RowGroupingModule } from '@ag-grid-enterprise/row-grouping'
import { MoreVert, Search } from '@mui/icons-material'
import { Box, IconButton, Menu, MenuItem, TextField, Typography } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  ColumnsToolPanelModule,
  FiltersToolPanelModule,
  RowGroupingModule,
  CsvExportModule,
  ExcelExportModule,
  ClipboardModule,
  RangeSelectionModule,
  MenuModule,
])

// Raw (unformatted) cell value — shared by export and copy.
const clipboardCellValue = (p: any) => {
  const field = p.column?.getColDef?.()?.field
  return field && p.node?.data && p.node.data[field] !== undefined ? p.node.data[field] : p.value
}

// MUI FilterAltIcon SVG, replaces ag-grid's default 3-lines filter glyph
const FILTER_ICON_SVG =
  '<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M7 6h10l-5.01 6.3L7 6zm-2.75-.39C6.27 8.2 10 13 10 13v6c0 .55.45 1 1 1h2c.55 0 1-.45 1-1v-6s3.72-4.8 5.74-7.39c.51-.66.04-1.61-.79-1.61H5.04c-.83 0-1.3.95-.79 1.61z"/></svg>'

interface FactorRiskTableProps {
  factors: FactorContrib[] | undefined
  selectedFactor: FactorLensFocus | null
  loading?: boolean
  cardPrefs?: RADashboardCardPrefs
  onCardPrefsChange?: (newPrefs: RADashboardCardPrefs) => void
}

const PERCENT_BAR_POSITIVE_COLOR = '#3b82f6'
const PERCENT_BAR_NEGATIVE_COLOR = COMPANY_COLORS.ERROR

interface PercentBarRendererParams extends ICellRendererParams {
  negativeColor?: string
  positiveColor?: string
}

const PercentBarRenderer = (params: PercentBarRendererParams) => {
  const value = params.value as number | null
  if (value == null || isNaN(value)) return <span style={{ color: 'inherit' }}>—</span>
  const isNegative = value < 0
  const pct = Math.min(Math.abs(value), 100)
  const barColor = isNegative
    ? params.negativeColor ?? PERCENT_BAR_NEGATIVE_COLOR
    : params.positiveColor ?? PERCENT_BAR_POSITIVE_COLOR
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 8,
        width: '100%',
        height: '100%',
      }}
    >
      <span style={{ fontSize: 11 }}>{value.toFixed(2)}%</span>
      <div
        style={{
          width: 80,
          flexShrink: 0,
          height: 12,
          backgroundColor: '#e2e8f0',
          borderRadius: 3,
          overflow: 'hidden',
          display: 'flex',
          justifyContent: isNegative ? 'flex-end' : 'flex-start',
        }}
      >
        <div style={{ width: `${pct}%`, height: '100%', backgroundColor: barColor, borderRadius: 3 }} />
      </div>
    </div>
  )
}

const FactorRiskTable: React.FC<FactorRiskTableProps> = ({
  factors = [],
  selectedFactor = null,
  loading = false,
  cardPrefs,
  onCardPrefsChange,
}) => {
  const theme = useTheme()
  const isDarkMode = useDarkModeCheck()
  const gridRef = useRef<AgGridReact>(null)
  const gridBoxRef = useRef<HTMLDivElement>(null)
  useClearCellRangeOnOutsideClick(gridBoxRef, () => gridRef.current?.api)
  const gridContextMenuItems = useMemo(() => buildCopyContextMenuItems({ processCell: clipboardCellValue }), [])
  const [searchTerm, setSearchTerm] = useState('')
  const [gridApi, setGridApi] = useState<any>(null)
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null)

  // Stable refs so save/apply callbacks never need to be recreated.
  const cardPrefsRef = useRef(cardPrefs)
  const onCardPrefsChangeRef = useRef(onCardPrefsChange)
  useEffect(() => {
    cardPrefsRef.current = cardPrefs
  }, [cardPrefs])
  useEffect(() => {
    onCardPrefsChangeRef.current = onCardPrefsChange
  }, [onCardPrefsChange])

  // Track which dashboard's prefs we've applied so save events don't re-trigger apply.
  const initializedForDashboard = useRef<string | null>(null)
  // Suppress save events that fire async from applyColumnState / setFilterModel.
  const applyingRef = useRef(false)

  // Strip properties that conflict with the treeData hierarchy (rowGroup/pivot)
  // and the synthetic auto-group column that owns row expansion.
  const sanitizeColumnState = (state: ColumnState[]): ColumnState[] =>
    state
      .filter((c) => c.colId !== 'ag-Grid-AutoColumn')
      .map((c) => ({
        colId: c.colId,
        hide: c.hide,
        width: c.width,
        flex: c.flex,
        pinned: c.pinned,
        sort: c.sort,
        sortIndex: c.sortIndex,
      }))

  const applyGridPrefs = useCallback((saved: FactorLensPrefs) => {
    const api = gridRef.current?.api
    if (!api) return
    applyingRef.current = true
    if (saved.factorTableColumnState) {
      api.applyColumnState({
        state: sanitizeColumnState(saved.factorTableColumnState as unknown as ColumnState[]),
        applyOrder: true,
      })
    }
    if (saved.factorTableFilterModel) {
      api.setFilterModel(saved.factorTableFilterModel)
    }
    setTimeout(() => {
      applyingRef.current = false
    }, 0)
  }, [])

  const saveGridState = useCallback(() => {
    if (applyingRef.current) return
    const api = gridRef.current?.api
    const prefs = cardPrefsRef.current
    if (!api || !prefs?.dashboardName) return
    const sanitized = sanitizeColumnState(api.getColumnState())
    const updated: RADashboardCardPrefs = {
      ...prefs,
      factorLens: {
        ...prefs.factorLens,
        factorTableColumnState: sanitized as unknown as Record<string, unknown>[],
        factorTableFilterModel: api.getFilterModel() as Record<string, unknown>,
      },
    }
    onCardPrefsChangeRef.current?.(updated)
    putUserPreference(cardPrefsContextKey(prefs.dashboardName), updated)
  }, [])

  const onColumnMoved = useCallback(
    (e: ColumnMovedEvent) => {
      if (e.finished) saveGridState()
    },
    [saveGridState],
  )

  const onToolPanelVisibleChanged = useCallback(
    (e: ToolPanelVisibleChangedEvent) => {
      if (!e.visible) saveGridState()
    },
    [saveGridState],
  )

  // Apply when the dashboard switches (not on every save).
  useEffect(() => {
    const dashboardName = cardPrefs?.dashboardName
    const saved = cardPrefs?.factorLens
    if (saved && gridRef.current?.api && dashboardName !== initializedForDashboard.current) {
      initializedForDashboard.current = dashboardName ?? null
      applyGridPrefs(saved)
    }
  }, [cardPrefs, applyGridPrefs])

  const total = useMemo(() => factors.reduce((sum, f) => sum + (f.risk_contrib ?? 0), 0), [factors])

  const columnDefs: ColDef[] = useMemo(
    () => [
      // Tree structure is driven by getDataPath on the grid — no rowGroup columns here.
      // Exposure — dashes for group rows and for null leaf values
      {
        headerName: 'Exposure',
        field: 'weighted_exposure',
        flex: 1,
        minWidth: 100,
        filter: 'agNumberColumnFilter',
        cellClass: 'ag-right-aligned-cell',
        valueFormatter: (params) => {
          if (params.node?.group || params.value == null) return '—'
          return Number(params.value).toFixed(2)
        },
      },
      // Factor Risk — dashes for group rows and for null leaf values
      {
        headerName: 'Factor Risk',
        field: 'factor_risk',
        flex: 1,
        minWidth: 100,
        filter: 'agNumberColumnFilter',
        cellClass: 'ag-right-aligned-cell',
        valueFormatter: (params) => {
          if (params.node?.group || params.value == null) return '—'
          return Number(params.value).toFixed(2)
        },
      },
      // Contribution to Risk — aggregated sum, blue color
      {
        headerName: getHeader(GfrItem.SecurityRiskContrib),
        field: 'risk_contrib',
        flex: 1,
        minWidth: 130,
        filter: 'agNumberColumnFilter',
        aggFunc: 'sum',
        cellClass: 'ag-right-aligned-cell',
        valueFormatter: (params) => (params.value != null ? Number(params.value).toFixed(2) : '0.00'),
        cellStyle: () => ({ color: '#3b82f6', fontWeight: 600 }),
      },
      // % of Total — progress bar renderer, computed from context.total
      {
        colId: 'pctOfTotal',
        headerName: '% of Total',
        flex: 1,
        minWidth: 180,
        sortable: false,
        valueGetter: (params) => {
          if (total === 0) return null
          const contrib: number = params.node?.group
            ? (params.node.aggData?.risk_contrib ?? 0)
            : (params.data?.risk_contrib ?? 0)
          return (contrib / total) * 100
        },
        cellRenderer: PercentBarRenderer,
        cellRendererParams: {
          positiveColor: PERCENT_BAR_POSITIVE_COLOR,
          negativeColor: PERCENT_BAR_NEGATIVE_COLOR,
        },
      },
      // Hidden, export-only columns so CSV/Excel produce a flat table (one row per
      // factor with each field in its own column). Not shown in the grid or tool panel.
      { colId: 'level1', field: 'level1', headerName: 'Risk Type', hide: true, suppressColumnsToolPanel: true },
      {
        colId: 'factor_category',
        field: 'factor_category',
        headerName: 'Category',
        hide: true,
        suppressColumnsToolPanel: true,
      },
      {
        colId: 'factor_sub_category',
        field: 'factor_sub_category',
        headerName: 'Sub-Category',
        hide: true,
        suppressColumnsToolPanel: true,
      },
      { colId: 'factor_name', field: 'factor_name', headerName: 'Factor', hide: true, suppressColumnsToolPanel: true },
      {
        colId: 'currency_code',
        field: 'currency_code',
        headerName: 'Currency',
        hide: true,
        suppressColumnsToolPanel: true,
      },
    ],
    [theme, total],
  )

  const autoGroupColumnDef: ColDef = useMemo(
    () => ({
      headerName: 'Factor',
      minWidth: 180,
      flex: 1.5,
      cellRendererParams: { suppressCount: true },
    }),
    [],
  )

  const defaultColDef: ColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      suppressMenu: false,
      floatingFilter: false,
    }),
    [],
  )

  const onGridReady = useCallback(
    (params: GridReadyEvent) => {
      setGridApi(params.api)
      const prefs = cardPrefsRef.current
      const saved = prefs?.factorLens
      if (saved) {
        initializedForDashboard.current = prefs?.dashboardName ?? null
        applyGridPrefs(saved)
      }
    },
    [applyGridPrefs],
  )

  const handleSearch = useCallback(
    (searchValue: string) => {
      if (searchValue.trim()) {
        const validation = validateInput(searchValue.trim(), {
          fieldName: 'Search',
          maxLength: 100,
          allowedPattern: /^[a-zA-Z0-9\s\-_()]+$/,
          blockedChars: ['&', '%', '#', '@', ';', '"', "'", '\\', '/', '*', '<', '>'],
        })
        if (!validation.isValid) {
          setSearchTerm('')
          if (gridApi) gridApi.setGridOption('quickFilterText', '')
          return
        }
      }
      setSearchTerm(searchValue)
      if (gridApi) gridApi.setGridOption('quickFilterText', searchValue)
    },
    [gridApi],
  )

  const rowData = useMemo(() => {
    // Aggregate records sharing the same [level2-5] path so AG-Grid treeData
    // doesn't silently drop "duplicate" paths. Specific Risk is already
    // aggregated upstream and passed through as a single synthetic row.
    const merged = new Map<string, FactorContrib>()
    factors.forEach((f) => {
      const key = f.factor_name === 'Specific Risk' ? `__SR__` : `${f.level2}|${f.level3}|${f.level4}|${f.level5}`
      const existing = merged.get(key)
      if (existing) {
        merged.set(key, {
          ...existing,
          risk_contrib: (existing.risk_contrib ?? 0) + (f.risk_contrib ?? 0),
          weighted_exposure:
            existing.weighted_exposure != null && f.weighted_exposure != null
              ? existing.weighted_exposure + f.weighted_exposure
              : (existing.weighted_exposure ?? f.weighted_exposure),
          factor_risk: existing.factor_risk ?? f.factor_risk,
        })
      } else {
        merged.set(key, { ...f })
      }
    })
    return Array.from(merged.values()).map((factor, index) => ({
      ...factor,
      uniqueId: `${factor.factor_name}-${index}`,
    }))
  }, [factors])

  const handleExport = (type: 'CSV' | 'EXCEL') => {
    const api = gridRef.current?.api
    if (!api) {
      setExportMenuAnchor(null)
      return
    }
    if (type === 'EXCEL') {
      // Excel keeps the grouped / tree view — Excel supports collapsible row
      // groups, and ag-grid's Excel export carries the hierarchy + outline.
      api.exportDataAsExcel({
        fileName: 'factor_breakdown',
        sheetName: 'factor-risk-data',
      })
      setExportMenuAnchor(null)
      return
    }
    // CSV is a flat table
    api.exportDataAsCsv({
      fileName: 'factor_breakdown.csv',
      columnKeys: [
        'level1',
        'factor_category',
        'factor_sub_category',
        'factor_name',
        'currency_code',
        'weighted_exposure',
        'factor_risk',
        'risk_contrib',
        'pctOfTotal',
      ],
      shouldRowBeSkipped: (p: any) => !!p.node?.group || !!p.node?.footer,
      processCellCallback: clipboardCellValue,
    })
    setExportMenuAnchor(null)
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: `1px solid ${theme.border}`,
        borderRadius: 1,
        backgroundColor: theme.cardBackground,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          backgroundColor: theme.headerBackground,
          borderBottom: `1px solid ${theme.border}`,
          p: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Typography variant='h2' sx={{ fontSize: '24px', fontWeight: 'bold', color: theme.textPrimary }}>
            {selectedFactor ? `${selectedFactor.name} - ` : ''}Factor Breakdown
          </Typography>
          <Typography sx={{ fontSize: '14px', color: theme.textSecondary, mt: 0.25 }}>
            Expand any row to drill down from Level 2 to Level 5
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <TextField
            size='small'
            placeholder='Search...'
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            InputProps={{
              startAdornment: <Search sx={{ mr: 1, color: theme.textSecondary }} />,
              sx: {
                backgroundColor: theme.inputBackground,
                color: theme.textPrimary,
                '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.border },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.textSecondary },
                '& input': { color: theme.textPrimary },
                '& input::placeholder': { color: theme.textSecondary, opacity: 1 },
              },
            }}
            sx={{ minWidth: 200 }}
          />
          {/* Export menu (3-dot) — CSV / Excel of the factor breakdown */}
          <IconButton
            size='small'
            onClick={(e) => setExportMenuAnchor(e.currentTarget)}
            sx={{ color: theme.textSecondary, padding: '4px' }}
          >
            <MoreVert sx={{ fontSize: 18 }} />
          </IconButton>
          <Menu
            anchorEl={exportMenuAnchor}
            open={Boolean(exportMenuAnchor)}
            onClose={() => setExportMenuAnchor(null)}
            PaperProps={{
              sx: {
                backgroundColor: theme.cardBackground,
                border: `1px solid ${theme.border}`,
                boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                minWidth: 140,
              },
            }}
          >
            <MenuItem
              onClick={() => handleExport('EXCEL')}
              sx={{ fontSize: '14px', color: theme.textPrimary, '&:hover': { backgroundColor: theme.border } }}
            >
              Export as Excel
            </MenuItem>
            <MenuItem
              onClick={() => handleExport('CSV')}
              sx={{ fontSize: '14px', color: theme.textPrimary, '&:hover': { backgroundColor: theme.border } }}
            >
              Export as CSV
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {/* AG-Grid */}
      <Box
        ref={gridBoxRef}
        sx={[
          {
            flex: 1,
            width: '100%',
            overflow: 'hidden',
            // cwan theme drives colors; keep grid-specific tokens only.
            '--ag-secondary-foreground-color': theme.textSecondary,
            '--ag-row-hover-color': getColorWithOpacity(theme.border, 0.4),
            '--ag-input-focus-border-color': theme.border,
            '--ag-checkbox-unchecked-color': theme.textSecondary,
            '--ag-checkbox-checked-color': theme.textPrimary,
            '--ag-input-border-color': theme.border,
            '--ag-modal-overlay-background-color': getColorWithOpacity(theme.cardBackground, 0.66),
            '--ag-control-panel-background-color': theme.cardBackground,
            '--ag-disabled-foreground-color': getColorWithOpacity(theme.textSecondary, 0.5),
            '--ag-chip-background-color': theme.buttonBackground,
            '--ag-pagination-bg-color': theme.cardBackground,
            '& .ag-row-footer': {
              backgroundColor: theme.border + '55',
              fontWeight: '600',
            },
            '& .ag-row-level--1.ag-row-footer': {
              backgroundColor: theme.border + 'bb',
              fontWeight: '700',
              borderTop: `2px solid ${theme.border}`,
            },
          } as React.CSSProperties,
          gridHeaderIconsSx,
        ]}
        className={getGridThemeClass(isDarkMode)}
      >
        <AgGridReact
          ref={gridRef}
          containerStyle={{ height: '100%', width: '100%' }}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          autoGroupColumnDef={autoGroupColumnDef}
          onGridReady={onGridReady}
          onColumnMoved={onColumnMoved}
          onSortChanged={saveGridState}
          onToolPanelVisibleChanged={onToolPanelVisibleChanged}
          sideBar={sideBarDefaultConfig}
          columnHoverHighlight
          enableRangeSelection
          popupParent={document.body}
          getContextMenuItems={gridContextMenuItems}
          processCellForClipboard={clipboardCellValue}
          icons={{ filter: () => FILTER_ICON_SVG }}
          treeData
          getDataPath={(data) => {
            if (data.factor_name === 'Specific Risk') return ['Specific Risk']
            // Truncate the path at the first null/empty level so records with
            // sparse level fields still render (at a shallower depth).
            const full = [data.level2, data.level3, data.level4, data.level5]
            const firstEmpty = full.findIndex((v) => v == null || v === '')
            const path = firstEmpty === -1 ? full : full.slice(0, firstEmpty)
            return path.length > 0 ? path : [data.factor_name ?? 'Unknown']
          }}
          getRowId={(params) => {
            if (params.data.factor_name === 'Specific Risk') return 'Specific Risk'
            return (
              [params.data.level2, params.data.level3, params.data.level4, params.data.level5]
                .filter((v) => v != null && v !== '')
                .join('|') ||
              (params.data.factor_name ?? 'Unknown')
            )
          }}
          groupDefaultExpanded={0}
          grandTotalRow='bottom'
          suppressAggFuncInHeader
          suppressDragLeaveHidesColumns
          animateRows={false}
          pagination
          paginationPageSize={50}
          paginationPageSizeSelector={[25, 50, 100, 200]}
          loading={loading}
          loadingOverlayComponent={() => (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Typography sx={{ color: theme.textPrimary }}>Loading data...</Typography>
            </Box>
          )}
          noRowsOverlayComponent={() => (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Typography sx={{ color: theme.textSecondary }}>
                {searchTerm ? `No data found matching "${searchTerm}"` : 'No data found'}
              </Typography>
            </Box>
          )}
        />
      </Box>
    </Box>
  )
}

export default FactorRiskTable
